/**
 * SectionPage — generic wrapper for section routes.
 *
 * Handles the deep-link pattern shared by all 4 sections:
 *   - If at the section index route with query params → render the Builder
 *   - If at the section index with a saved builder URL → redirect back to it
 *   - Otherwise → render child routes via <Outlet />
 */
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import type { ComponentType } from 'react';
import { getLastSectionUrl } from '@/lib/sectionLastUrl';

interface SectionPageProps {
  /** The base path for this section (e.g., '/purchases'). */
  basePath: string;
  /** The Builder component to render when deep-linking with query params. */
  BuilderComponent: ComponentType;
  /** Query param keys that indicate a deep-link (presence of any triggers the builder). */
  queryParamKeys: string[];
}

export function SectionPage({ basePath, BuilderComponent, queryParamKeys }: SectionPageProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isIndex = location.pathname === basePath;
  const hasQueryParams = queryParamKeys.some((key) => searchParams.has(key));

  if (isIndex && hasQueryParams) {
    return <BuilderComponent />;
  }

  // Restore last builder URL when returning to the section from outside
  // (e.g. sidebar click). The saved URL is cleared by BuilderShell's back
  // button, so clicking "Back" still takes you to the reports listing.
  if (isIndex && !hasQueryParams) {
    const lastUrl = getLastSectionUrl(basePath);
    if (lastUrl && lastUrl !== basePath) {
      return <Navigate to={lastUrl} replace />;
    }
  }

  return <Outlet />;
}
